import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  onSendFeedback: (comment: string) => Promise<void>;
}

export function FeedbackDialog({ open, onClose, onSendFeedback }: FeedbackDialogProps) {
  const [comment, setComment] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setComment('');
    setSent(false);
    setError(null);
    setSending(false);
  }, [open]);

  const sendFeedback = async () => {
    if (sending) {
      return;
    }

    setSending(true);
    setError(null);

    try {
      await onSendFeedback(comment);
      setComment('');
      setSent(true);
    } catch {
      setError('Unable to send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !sending && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Feedback</DialogTitle>
      <DialogContent>
        {sent ? (
          <Box sx={{ py: 1 }}>Thank you! Your feedback is really appreciated.</Box>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body1">
              Your comments and suggestions will be used to make dotstorming better!
            </Typography>
            <TextField
              autoFocus
              disabled={sending}
              fullWidth
              label="Please enter your comment"
              minRows={5}
              multiline
              onChange={(event) => setComment(event.target.value)}
              value={comment}
            />
            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {sent ? (
          <Button onClick={onClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button disabled={sending} onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={sending} onClick={() => void sendFeedback()} variant="contained">
              Send
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
